import React, { useState, useRef, useEffect } from 'react';
import styles from './Dropdown.module.css';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  multiSelect?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  className = '',
  multiSelect = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to check if a value is selected
  const isSelected = (optionValue: string): boolean => {
    if (multiSelect && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  // Get display text based on single or multi select
  const getDisplayText = (): string => {
    if (multiSelect && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        const option = options.find(opt => opt.value === value[0]);
        return option?.label || placeholder;
      }
      return `${value.length} selected`;
    }
    
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const displayText = getDisplayText();
  const hasSelection = multiSelect 
    ? Array.isArray(value) && value.length > 0 
    : Boolean(value);

  const filteredOptions = options.filter(option => {
    const label = option?.label || '';
    const search = searchTerm?.toLowerCase() || '';
    return label.toLowerCase().includes(search);
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      
      if (currentValues.includes(optionValue)) {
        // Remove from selection
        onChange(currentValues.filter(v => v !== optionValue));
      } else {
        // Add to selection
        onChange([...currentValues, optionValue]);
      }
      // Don't close dropdown in multiselect mode
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiSelect ? [] : '');
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div 
      ref={dropdownRef} 
      className={`${styles.customDropdown} ${className} ${disabled ? styles.disabled : ''}`}
    >
      <div 
        className={`${styles.dropdownHeader} ${isOpen ? styles.open : ''}`}
        onClick={handleToggle}
      >
        <span className={hasSelection ? styles.selectedText : styles.placeholderText}>
          {displayText}
        </span>
        <div className={styles.headerIcons}>
          {hasSelection && !disabled && (
            <button
              className={styles.clearButton}
              onClick={handleClearAll}
              aria-label="Clear selection"
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ''}`}
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          {options.length > 5 && (
            <div className={styles.searchContainer}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.searchIcon}>
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          
          <div className={styles.optionsList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noOptions}>No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${isSelected(option.value) ? styles.selected : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected(option.value)}
                      onChange={() => {}}
                      className={styles.checkbox}
               
                    />
                  )}
                  <span className={styles.optionLabel}>{option.label}</span>
                  {!multiSelect && isSelected(option.value) && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.checkIcon}>
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>

          {multiSelect && (
            <div className={styles.dropdownFooter}>
              <button
                className={styles.doneButton}
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                type="button"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;